import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Collapse, Row, Col, Button, Upload, message, Modal, Spin } from "antd";
import { RightOutlined } from "@ant-design/icons";
import client from "../kairosClient";
import { useAuthState } from "../providers/AuthProvider";
import * as XLSX from "xlsx";
import { Loading } from "../pages";
import { BaseButton } from "../components";
import { useHistory } from "react-router-dom";

const RegistrationCompany = () => {
  const { t } = useTranslation();
  const { Panel } = Collapse;
  const { token } = useAuthState();
  const history = useHistory();

  const [isLoading, setLoading] = useState(false);
  const [dati, setDati] = useState([]);
  const [usersByCompany, setUsersByCompany] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleFileUpload = (file, companyId) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      const arrayBuffer = e.target.result;

      try {
        const workbook = XLSX.read(new Uint8Array(arrayBuffer), {
          type: "array",
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(sheet);

        setUploading(true);
        const response = await client.user.uploadExcel(companyId, data, token);

        if (response.ok) {
          const result = await response.json();
          setUploadResult(result); // 👈 salva il risultato
          setIsModalVisible(true); // 👈 apri modale
          setTimeout(() => setIsModalVisible(false), 3000);
        } else {
          message.error(t("file_upload_failed"));
        }
      } catch (error) {
        setUploadResult({ error: true });
        message.error(t("file_upload_error"));
        console.error("Error uploading file:", error);
      } finally {
        setUploading(false);
        setTimeout(() => setUploadResult(null), 3000);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const loadUsersForCompany = async (companyId) => {
    try {
      const response = await client.user.getByCompanyId(companyId, token);
      const data = await response.json();

      const sortedData = data.sort((a, b) => {
        const surnameA = a.surname?.toLowerCase() || "";
        const surnameB = b.surname?.toLowerCase() || "";
        const nameA = a.name?.toLowerCase() || "";
        const nameB = b.name?.toLowerCase() || "";

        return surnameA.localeCompare(surnameB) || nameA.localeCompare(nameB);
      });

      setUsersByCompany((prev) => ({ ...prev, [companyId]: sortedData }));
    } catch (err) {
      message.error(t("error_loading_users"));
      console.error("Error fetching users:", err);
    }
  };

  React.useEffect(() => {
    setLoading(true);
    client.company
      .getAll(token)
      .then((res) => res.json())
      .then((res) => setDati(res))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading || uploading) {
    return <Loading />;
  }
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {uploading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9999,
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" />
        </div>
      )}

      <Modal
        visible={isModalVisible}
        footer={null}
        closable={false}
        centered
        bodyStyle={{ padding: "30px", textAlign: "center" }}
      >
        <div style={{ textAlign: "center" }}>
          <p>{t("file_uploaded_successfully")}</p>
          {uploadResult && (
            <p>
              ✅ {t("created")}: <strong>{uploadResult.created || 0}</strong>{" "}
              <br />
              🔁 {t("updated")}: <strong>{uploadResult.updated || 0}</strong>
            </p>
          )}
        </div>
      </Modal>

      <div
        className="collapse-style"
        style={{
          backgroundColor: "#FFF",
          borderRadius: "10px",
          padding: "0px 20px",
          marginBottom: "20px",
          minWidth: "1240px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "20px 0px",
            borderBottom: "2px solid #E3EEF3",
          }}
        >
          <div>
            <BaseButton
              className="shadow-button"
              type="primary"
              shape="round"
              htmlType="button"
              onClick={() => history.push("/companysignup")}
              style={{ marginBottom: "0px" }}
            >
              <span>{t("label_add").toUpperCase()}</span>
            </BaseButton>
          </div>
        </div>
        <div>
          <Row justify="center">
            <Col xl={{ span: 24 }} xs={{ span: 22 }}>
              <div
                style={{
                  margin: "20px 40px 0px 40px",
                  padding: "0px 16px",
                  display: "grid",
                  gridTemplateColumns: "1fr 360px 250px 100px",
                  columnGap: "24px",
                  fontSize: "20px",
                  textTransform: "uppercase",
                  color: "#035E86",
                }}
              >
                <strong>{t("label_company_name")}</strong>
                <strong>{t("label_email")}</strong>
                <strong>{t("label_code")}</strong>
                <strong>{t("label_select_company_type")}</strong>
              </div>
            </Col>
          </Row>
          {dati.length > 0 ? (
            dati.map((d) => (
              <Collapse
                key={`collapse-${d.id}`} // solo per React
                style={{ width: "100%" }}
                className="collapse-style"
                defaultActiveKey={[]}
                expandIconPosition={"right"}
                expandIcon={({ isActive }) => (
                  <RightOutlined
                    style={{ fontSize: "30px", color: "#fff" }}
                    rotate={isActive ? 90 : 0}
                  />
                )}
                onChange={async (activeKeys) => {
                  const stringId = String(d.id);
                  if (activeKeys.includes(stringId)) {
                    await loadUsersForCompany(d.id);
                  }
                }}
              >
                <Panel
                  key={`${d.id}`}
                  style={{
                    backgroundColor: "#004AAC",
                    borderRadius: "10px",
                    borderBottom: "0px solid",
                  }}
                  header={
                    <Row justify="center">
                      <Col xl={{ span: 24 }} xs={{ span: 22 }}>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 360px 250px 100px",
                            columnGap: "24px",
                            fontSize: "20px",
                            margin: "0px 16px 0px 40px",
                            color: "#fff",
                          }}
                        >
                          <span style={{ textTransform: "uppercase" }}>
                            {d.name}
                          </span>
                          <span>{d.email}</span>
                          <span>{d.code}</span>
                          <span style={{ textTransform: "uppercase" }}>
                            {d.typeId === 1
                              ? t("school")
                              : t("select_form_company_type_company")}
                          </span>
                        </div>
                      </Col>
                    </Row>
                  }
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 360px 250px 100px",
                      columnGap: "20px",
                      fontSize: "18px",
                      margin: "0px 40px 0px 40px",
                    }}
                  >
                    <div>
                      <strong>{t("label_address").concat(": ")}</strong>
                      {d.address}
                    </div>
                    <div>
                      <strong>{t("label_city").concat(": ")}</strong>
                      {d.city}
                    </div>
                    <div>
                      <strong>
                        {d.typeId === 1
                          ? `${t("abbr_mechanographic_code")}`
                          : `${t("abbr_vat_number")}`}
                        :{" "}
                      </strong>
                      <span style={{ textTransform: "uppercase" }}>
                        {d.vatNumber}
                      </span>
                    </div>
                  </div>

                  {/* Bottone per caricare il file Excel */}
                  <div
                    style={{
                      padding: "20px 0px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Upload
                      customRequest={({ file, onSuccess }) => {
                        handleFileUpload(file, d.id);
                        onSuccess();
                      }}
                      showUploadList={false}
                      accept=".xlsx, .xls"
                    >
                      <Button type="primary" shape="round">
                        {t("upload_users_file")}
                      </Button>
                    </Upload>
                  </div>
                  {usersByCompany[d.id] && (
                    <div style={{ marginTop: "20px", padding: "0 40px" }}>
                      <h3>{`${t("users_list")} - tot ${
                        usersByCompany[d.id].length
                      }`}</h3>
                      {usersByCompany[d.id].length > 0 ? (
                        <ul>
                          {usersByCompany[d.id].map((u) => (
                            <li key={u.email}>
                              {u.surname} {u.name} — {u.email}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>{t("no_users_found")}</p>
                      )}
                    </div>
                  )}
                </Panel>
              </Collapse>
            ))
          ) : (
            <div>{t("no_search_result").toUpperCase()}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationCompany;
